/** One-screen best/worst selection (spec 36-02). List order IS the rank. See ADR-0002 §2. */

// Mirrors `MAXDIFF_MODE_MULTI_SET` / `MAXDIFF_MODE_BEST_WORST` in the backend's
// `constants/interview_constants.py` — pinned by `bestWorstModeConstantsMatchBackend`
// (`__tests__/bestWorst.test.ts`) and `test_frontend_mode_constants_match` (backend).
export const MAXDIFF_MODE_MULTI_SET = 'multi_set';
export const MAXDIFF_MODE_BEST_WORST = 'best_worst_single_set';

export interface BestWorstState {
  best: string[];
  worst: string[];
}

export interface Quotas {
  nBest: number;
  nWorst: number;
}

export type BestWorstSide = 'best' | 'worst';

export interface Anchors {
  best: string;
  worst: string;
}

// The shipped literals, so an untouched survey is byte-identical on screen (ADR-0003 §2).
export const ANCHOR_DEFAULTS: Anchors = { best: 'Most Appealing', worst: 'Least Appealing' };

/** Long enough for "Least likely to buy"; short enough for a rail header on a 360px phone. */
export const ANCHOR_MAX_LENGTH = 28;

export function emptyState(): BestWorstState {
  return { best: [], worst: [] };
}

const without = (list: string[], option: string) => list.filter((o) => o !== option);

/** The words the respondent sees, for either MaxDiff mode. Guarded here, not on save — ADR-0003 §2. */
export function resolveAnchors(
  config?: { best_label?: string; worst_label?: string } | null,
): Anchors {
  // Trimmed AGAIN after the cut: the cap can land on a space mid-label, and a header ending in one
  // renders with a stray gap before its pips.
  const one = (raw: string | undefined, fallback: string) =>
    (raw ?? '').trim().slice(0, ANCHOR_MAX_LENGTH).trim() || fallback;
  return {
    best: one(config?.best_label, ANCHOR_DEFAULTS.best),
    worst: one(config?.worst_label, ANCHOR_DEFAULTS.worst),
  };
}

/** Put one option on one side, or take it off it. A full side refuses — ADR-0003 §1. */
export function setSide(
  state: BestWorstState,
  option: string,
  side: BestWorstSide,
  quotas: Quotas,
): BestWorstState {
  const quota = side === 'best' ? quotas.nBest : quotas.nWorst;

  if (state[side].includes(option)) {
    return side === 'best'
      ? { ...state, best: without(state.best, option) }
      : { ...state, worst: without(state.worst, option) };
  }
  if (state[side].length >= quota) return state;

  // Per side, not computed keys: the cast that would silence TS also hides a wrong key.
  return side === 'best'
    ? { best: [...state.best, option], worst: without(state.worst, option) }
    : { best: without(state.best, option), worst: [...state.worst, option] };
}

/**
 * An exercise that asks for NOTHING is never answered: both quotas 0 used to read as finished and
 * record an empty answer against a question nobody was asked. One-sided is untouched.
 */
export function isComplete(state: BestWorstState, quotas: Quotas): boolean {
  if (quotas.nBest <= 0 && quotas.nWorst <= 0) return false;
  return state.best.length === quotas.nBest && state.worst.length === quotas.nWorst;
}

export function toResponseValue(state: BestWorstState) {
  const rank = (list: string[]) => list.map((option, i) => ({ option, rank: i + 1 }));
  return { best: rank(state.best), worst: rank(state.worst) };
}

/** One option on one side of a stored answer, as `toResponseValue` writes it. */
export interface BestWorstRankedOption {
  option: string;
  rank: number;
}

export interface BestWorstAnswer {
  best: BestWorstRankedOption[];
  worst: BestWorstRankedOption[];
}

/** Reading order within a side, and between the two sides, when the answer is written as one line. */
const RANKED_OPTION_SEPARATOR = ', ';
const BEST_WORST_SIDE_SEPARATOR = ' · ';

const isRankedOption = (entry: unknown): boolean =>
  !!entry &&
  typeof entry === 'object' &&
  typeof (entry as { option?: unknown }).option === 'string';

/** One side in rank order, or `null` when this is not a side. An absent key is an EMPTY side, not
 *  a refusal — a one-sided exercise reaches here that way and is a readable answer. */
const readSide = (raw: unknown): BestWorstRankedOption[] | null => {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw) || !raw.every(isRankedOption)) return null;
  return raw
    .map((entry, index) => ({
      option: entry.option as string,
      rank: typeof entry.rank === 'number' ? entry.rank : index + 1,
    }))
    .sort((a, b) => a.rank - b.rank);
};

/**
 * The stored best/worst answer, or `null` for any other shape — the ONLY place it is decoded.
 * `null` rather than a half-built answer is what lets callers fall through to their own renderers.
 */
export function parseBestWorstAnswer(value: unknown): BestWorstAnswer | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const candidate = value as { best?: unknown; worst?: unknown };
  if (candidate.best === undefined && candidate.worst === undefined) return null;

  const best = readSide(candidate.best);
  const worst = readSide(candidate.worst);
  if (!best || !worst) return null;
  return { best, worst };
}

/**
 * The answer on one line under the researcher's anchors — `Would buy first: 1. Oat · Would skip:
 * 1. Rice`. An empty side is omitted; both empty gives `''`, which the caller shows as no answer.
 */
export function formatBestWorstAnswer(answer: BestWorstAnswer, anchors: Anchors): string {
  const side = (label: string, options: BestWorstRankedOption[]) =>
    options.length === 0
      ? ''
      : `${label}: ${options.map((o) => `${o.rank}. ${o.option}`).join(RANKED_OPTION_SEPARATOR)}`;

  return [side(anchors.best, answer.best), side(anchors.worst, answer.worst)]
    .filter(Boolean)
    .join(BEST_WORST_SIDE_SEPARATOR);
}

export type BestWorstStage = 'best' | 'worst' | 'review';

export interface BestWorstAsk {
  phase: BestWorstSide | 'review';
  /** `refill` is ONLY a settled side that reopened — ADR-0003 addendum. */
  kind: 'start' | 'continue' | 'refill';
  remaining: number;
}

const filled = (state: BestWorstState, side: BestWorstSide, quotas: Quotas) =>
  state[side].length >= (side === 'best' ? quotas.nBest : quotas.nWorst);

/** Forward only. A side finished once is never asked for from scratch again — ADR-0003 §5. */
export function advanceStage(
  state: BestWorstState,
  quotas: Quotas,
  stage: BestWorstStage,
): BestWorstStage {
  let next = stage;
  if (next === 'best' && filled(state, 'best', quotas)) next = 'worst';
  if (next === 'worst' && filled(state, 'worst', quotas)) next = 'review';
  return next;
}

/** What is still outstanding, and whether it is a first ask or a top-up — ADR-0003 §5. */
export function nextAsk(
  state: BestWorstState,
  quotas: Quotas,
  stage: BestWorstStage,
): BestWorstAsk {
  if (filled(state, 'best', quotas) && filled(state, 'worst', quotas)) {
    return { phase: 'review', kind: 'start', remaining: 0 };
  }
  if (stage === 'best') {
    return {
      phase: 'best',
      kind: state.best.length === 0 ? 'start' : 'continue',
      remaining: quotas.nBest - state.best.length,
    };
  }
  if (!filled(state, 'worst', quotas)) {
    return {
      phase: 'worst',
      kind: state.worst.length === 0 ? 'start' : 'continue',
      remaining: quotas.nWorst - state.worst.length,
    };
  }
  // Both stages behind us and the top short: a settled pick was dragged across.
  return { phase: 'best', kind: 'refill', remaining: quotas.nBest - state.best.length };
}

interface QuestionLike {
  type?: string;
  max_diff_config?: { mode?: string } | null;
}

/**
 * Best/worst is a MODE of `max_diff`; asking this is the only way to know one. The short-lived
 * separate type never shipped, so there is nothing stored to migrate.
 */
export function isBestWorstQuestion(question?: QuestionLike | null): boolean {
  if (!question) return false;
  return question.type === 'max_diff' && question.max_diff_config?.mode === MAXDIFF_MODE_BEST_WORST;
}
